import { useEffect, useState, useContext } from 'react';
import {
  PlusCircle,
  TrendingUp,
  TrendingDown,
  Wallet,
} from 'lucide-react';
import AddExpenseDrawer from '../components/AddExpenseDrawer';
import { AuthContext } from '../context/AuthContext';
import { listExpenses } from '../api/expenses';
import axios from "axios";
import { Link } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

export default function Payments() {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, isHydrating } = useContext(AuthContext);

  // ✅ Use same gating pattern as Analytics
  const { isPlusActive, isLoading } = useSubscription();

  useEffect(() => {
    // ✅ Wait for auth hydration first
    if (isHydrating) {
      return;
    }

    // ✅ No authenticated user yet
    if (!user) {
      setLoading(false);
      return;
    }

    // ✅ Don’t fetch until subscription status known, and only if Plus
    if (isLoading || !isPlusActive) {
      setLoading(false);
      return;
    }

    async function loadRealData() {
      setLoading(true);

      try {
        const [paymentsResult, expensesResult] = await Promise.allSettled([
          axios.get(`${API_BASE}/api/payments`, { withCredentials: true }),
          listExpenses(),
        ]);

        const paymentsRes =
          paymentsResult.status === "fulfilled"
            ? paymentsResult.value
            : { data: [] };

        const expensesRes =
          expensesResult.status === "fulfilled"
            ? expensesResult.value
            : { expenses: [] };

        if (paymentsResult.status === "rejected") {
          console.error("Payments fetch failed:", paymentsResult.reason);
        }

        if (expensesResult.status === "rejected") {
          console.error("Expenses fetch failed:", expensesResult.reason);
        }

        const rawPayments =
          paymentsRes?.data?.payments ||
          paymentsRes?.data?.data?.payments ||
          paymentsRes?.data ||
          [];

        const rawExpenses =
          expensesRes?.data?.expenses ||
          expensesRes?.data?.data?.expenses ||
          expensesRes?.expenses ||
          expensesRes?.data ||
          [];

        const normalizedPayments = (Array.isArray(rawPayments) ? rawPayments : []).map((p, idx) => {
          const tenant =
            p.tenant ||
            p.tenantName ||
            p?.tenant?.name ||
            p?.tenant?.fullName ||
            p?.tenant?.firstName ||
            (p?.tenant?.first_name ? `${p?.tenant?.first_name} ${p?.tenant?.last_name || ''}`.trim() : '') ||
            '—';

          const property =
            p.property ||
            p.propertyAddress ||
            p?.property?.address ||
            p?.property?.fullAddress ||
            p?.tenancy?.property?.address ||
            p?.tenancy?.property?.fullAddress ||
            '—';

          const amount = Number(
            p.amount ??
              p.amountGBP ??
              p.rentAmountGBP ??
              p.rentAmount ??
              p.total ??
              0
          ) || 0;

          const dueDate =
            p.dueDate ||
            p.due_date ||
            p.dueOn ||
            p.due_on ||
            p?.schedule?.dueDate ||
            p?.schedule?.due_date ||
            '';

          const lastPaid =
            p.paidDate ||
            p.lastPaid ||
            p.paidAt ||
            p.paid_at ||
            p.paidOn ||
            p.paid_on ||
            p?.paymentDate ||
            p?.payment_date ||
            '';

          const status =
            p.status === "Paid" || p.status === "Complete"
              ? "Complete"
              : p.status === "Overdue"
              ? "Overdue"
              : p.status || "";

          const method =
            p.method ||
            p.paymentMethod ||
            p.payment_method ||
            p.provider ||
            '';

          return {
            id: p.id || p.paymentId || p._id || `${idx}-${dueDate}-${amount}`,
            tenant,
            property,
            amount,
            dueDate,
            status,
            method,
            lastPaid,
          };
        });

        const normalizedExpenses = (Array.isArray(rawExpenses) ? rawExpenses : []).map((e, idx) => {
          const category = e.category || e.type || e.expenseType || '—';
          const description = e.description || e.note || e.details || '—';
          const amount = Number(e.amount ?? e.amountGBP ?? e.cost ?? 0) || 0;

          const date = e.date || e.createdAt || e.created_at || e.incurredOn || e.incurred_on || '';

          return {
            id: e.id || e.expenseId || e._id || `${idx}-${date}-${amount}`,
            category,
            description,
            amount,
            date,
          };
        });

        setPayments(normalizedPayments);
        setExpenses(normalizedExpenses);
      } catch (err) {
        console.error('Failed to load payments/expenses:', err);
        setPayments([]);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    }

    loadRealData();
  }, [user, isHydrating, isPlusActive, isLoading]);

  const handleAddExpense = (expense) => {
    setExpenses((prev) => [...prev, expense]);
  };

  const totalIncome = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const netProfit = totalIncome - totalExpenses;
  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // ✅ Match Analytics “checking subscription…” state
  if (isLoading) {
    return <div className="pt-10 text-white text-center">Checking subscription…</div>;
  }

  // ✅ Match Analytics paywall UI: blur background + centered upgrade modal
  if (!isPlusActive) {
    return (
      <div className="relative pt-10 md:pt-0 text-white">
        <div className="blur-md pointer-events-none">
          <div className="pt-10 md:pt-0 text-white relative">
            {/* HEADER */}
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-4xl">Payments & financial overview</h1>
              <button
                onClick={() => {}}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-xl hover:bg-[#AF9B8A]/20"
              >
                <PlusCircle className="w-5 h-5" />
                <span>Record Expense</span>
              </button>
            </div>

            <div className="h-px w-36 bg-[#AF9B8A] mb-8"></div>

            {/* SUMMARY CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md">
                <h3 className="text-gray-300 text-sm">Total Income</h3>
                <p className="text-2xl font-semibold mt-1">£0</p>
                <TrendingUp className="w-8 h-8 text-[#AF9B8A]" />
              </div>

              <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md">
                <h3 className="text-gray-300 text-sm">Total Expenses</h3>
                <p className="text-2xl font-semibold mt-1">£0</p>
                <TrendingDown className="w-8 h-8 text-red-400" />
              </div>

              <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md">
                <h3 className="text-gray-300 text-sm">Net Profit</h3>
                <p className="text-2xl font-semibold mt-1">£0</p>
                <Wallet className="w-8 h-8 text-green-400" />
              </div>

            </div>

            {/* PAYMENT HISTORY */}
            <h2 className="text-3xl mb-4">Payment History</h2>
            <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>

            <div className="overflow-x-auto bg-white/10 rounded-xl border border-white/10 backdrop-blur-md mb-12">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-300">
                    <th className="p-3 text-left">Tenant</th>
                    <th className="p-3 text-left">Property</th>
                    <th className="p-3 text-left">Amount</th>
                    <th className="p-3 text-left">Due Date</th>
                    <th className="p-3 text-left">Paid Date</th>
                    <th className="p-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="p-3">—</td>
                    <td className="p-3">—</td>
                    <td className="p-3">£0.00</td>
                    <td className="p-3">—</td>
                    <td className="p-3">—</td>
                    <td className="p-3">—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* EXPENSES */}
            <h2 className="text-3xl mb-4">Property Expenses</h2>
            <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>

            <div className="overflow-x-auto bg-white/10 rounded-xl border border-white/10 backdrop-blur-md mb-12">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-300">
                    <th className="p-3 text-left">Category</th>
                    <th className="p-3 text-left">Description</th>
                    <th className="p-3 text-left">Amount</th>
                    <th className="p-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="p-3">—</td>
                    <td className="p-3">—</td>
                    <td className="p-3">£0.00</td>
                    <td className="p-3">—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* RENTAL ARREARS */}
            <h2 className="text-3xl mb-4">Rental Arrears</h2>
            <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>

            <div className="overflow-x-auto bg-white/10 rounded-xl border border-white/10 backdrop-blur-md mb-12">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-300">
                    <th className="p-3 text-left">Tenant</th>
                    <th className="p-3 text-left">Amount</th>
                    <th className="p-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="p-3">—</td>
                    <td className="p-3">£0.00</td>
                    <td className="p-3">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="fixed inset-0 flex items-center justify-center">
          <div className="bg-black/70 p-8 rounded-2xl text-center">
            <p className="text-xl mb-4">Payments is a Beem Plus feature</p>
            {/* Same CTA pattern as Analytics */}
            <Link to="/settings" className="bg-[#AF9B8A] px-4 py-2 rounded-xl">
              Upgrade
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // From here down: your original page unchanged
  if (loading) {
    return (
      <div className="p-6 text-gray-300 animate-pulse">
        Loading payments…
      </div>
    );
  }

  return (
    <div className="pt-10 md:pt-0 text-white relative">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">Payments & financial overview</h1>
        <button
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-xl hover:bg-[#AF9B8A]/20"
        >
          <PlusCircle className="w-5 h-5" />
          <span>Record Expense</span>
        </button>
      </div>

      <div className="h-px w-36 bg-[#AF9B8A] mb-8"></div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md">
          <h3 className="text-gray-300 text-sm">Total Income</h3>
          <p className="text-2xl font-semibold mt-1">£{totalIncome.toLocaleString()}</p>
          <TrendingUp className="w-8 h-8 text-[#AF9B8A]" />
        </div>

        <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md">
          <h3 className="text-gray-300 text-sm">Total Expenses</h3>
          <p className="text-2xl font-semibold mt-1">£{totalExpenses.toLocaleString()}</p>
          <TrendingDown className="w-8 h-8 text-red-400" />
        </div>

        <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md">
          <h3 className="text-gray-300 text-sm">Net Profit</h3>
          <p className="text-2xl font-semibold mt-1">£{netProfit.toLocaleString()}</p>
          <Wallet className="w-8 h-8 text-green-400" />
        </div>

      </div>

      {/* PAYMENT HISTORY */}
      <h2 className="text-3xl mb-4">Payment History</h2>
      <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>

      <div className="overflow-x-auto bg-white/10 rounded-xl border border-white/10 backdrop-blur-md mb-12">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-gray-300">
              <th className="p-3 text-left">Tenant</th>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Due Date</th>
              <th className="p-3 text-left">Paid Date</th>
              <th className="p-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-white/10">
                <td className="p-3">{p.tenant}</td>
                <td className="p-3">{p.property}</td>
                <td className="p-3">£{Number(p.amount || 0).toFixed(2)}</td>
                <td className="p-3">{formatDate(p.dueDate)}</td>
                <td className="p-3">{formatDate(p.lastPaid)}</td>
                <td className="p-3">{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* EXPENSES */}
      <h2 className="text-3xl mb-4">Property Expenses</h2>
      <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>

      <div className="overflow-x-auto bg-white/10 rounded-xl border border-white/10 backdrop-blur-md mb-12">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-gray-300">
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Description</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} className="border-b border-white/10">
                <td className="p-3">{e.category}</td>
                <td className="p-3">{e.description}</td>
                <td className="p-3">£{Number(e.amount || 0).toFixed(2)}</td>
                <td className="p-3">{e.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* RENTAL ARREARS */}
      <h2 className="text-3xl mb-4">Rental Arrears</h2>
      <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>

      <div className="overflow-x-auto bg-white/10 rounded-xl border border-white/10 backdrop-blur-md mb-12">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-gray-300">
              <th className="p-3 text-left">Tenant</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {payments
              .filter((p) => p.status === 'Overdue')
              .map((p) => (
                <tr key={p.id} className="border-b border-white/10">
                  <td className="p-3">{p.tenant}</td>
                  <td className="p-3">£{Number(p.amount || 0).toFixed(2)}</td>
                  <td className="p-3">{formatDate(p.dueDate)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ADD EXPENSE DRAWER */}
      {drawerOpen && (
        <AddExpenseDrawer
          onSave={handleAddExpense}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </div>
  );
}
